<template>
  <v-container fluid class="pa-4 driver-dashboard">
    <!-- Driver Info Card -->
    <v-card class="mb-4 info-card" elevation="0">
      <v-card-text>
        <div class="d-flex align-center">
          <div>
            <v-icon left>mdi-account</v-icon>
            <span class="text-h6 font-weight-bold">Driver Information</span>
            <div class="text-h6 font-weight-bold">{{ driverInfo.name }}</div>
            <div class="text-caption grey--text">Email: {{ driverInfo.email }}</div>
          </div>
          <v-spacer />
        </div>
      </v-card-text>
    </v-card>

    <!-- Loading State -->
    <v-card v-if="loading" class="mb-4" elevation="0">
      <v-card-text class="text-center">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
        <div class="mt-2">Loading trips...</div>
      </v-card-text>
    </v-card>

    <!-- Empty State -->
    <v-card v-else-if="!loading && trips.length === 0" class="mb-4" elevation="0">
      <v-card-text class="text-center py-8">
        <v-icon size="64" color="grey lighten-1">mdi-truck-outline</v-icon>
        <div class="text-h6 mt-4 grey--text">No Active Trips</div>
        <div class="text-body-2 grey--text">You don't have any trips assigned at the moment.</div>
      </v-card-text>
    </v-card>

    <!-- Trips Tabs -->
    <v-card v-else class="trips-card" elevation="0">
      <v-tabs v-model="activeTab" background-color="transparent" color="primary" show-arrows>
        <v-tab v-for="(trip, index) in trips" :key="trip.id">
          <v-icon left small>mdi-truck</v-icon>
          Task #{{ index + 1 }}
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab">
        <v-tab-item v-for="trip in trips" :key="trip.id">
          <v-card-text class="pt-4">

            <!-- Assignment Details -->
            <v-card class="mb-4 assignment-card" elevation="0">
              <v-card-title class="pb-2">
                <v-icon color="#2563EB" class="mr-2">mdi-clipboard-text</v-icon>
                <span style="color: #2563EB;">Assignment Details</span>
              </v-card-title>
              <v-card-text>
                <v-row dense>
                  <!-- Company Info -->
                  <v-col cols="12">
                    <div class="text-caption grey--text">Company:</div>
                    <div class="text-body-1 font-weight-medium">{{ trip.calendar_event.company.name }}</div>
                    <div class="text-caption">{{ trip.calendar_event.contact_person }}</div>
                    <div class="text-caption">{{ trip.calendar_event.company.phone }}</div>
                  </v-col>

                  <!-- Vehicle Info -->
                  <v-col cols="12" class="mt-3">
                    <div class="text-caption grey--text">Vehicle to use:</div>
                    <div class="text-body-2 font-weight-medium" v-if="trip.vehicle">
                      {{ trip.vehicle.vehicle_number }} - {{ trip.vehicle.make }} {{ trip.vehicle.model }}
                    </div>
                    <div class="text-body-2 grey--text" v-if="!trip.vehicle">
                      Not assigned
                    </div>
                  </v-col>

                  <!-- Pickup Location -->
                  <v-col cols="12" class="mt-3">
                    <div class="text-caption grey--text">Pickup Location:</div>
                    <div class="text-body-2 font-weight-medium" v-if="trip.calendar_event.pickup_address">
                      {{ trip.calendar_event.pickup_address.address }}
                    </div>
                    <div class="text-caption grey--text" v-if="!trip.calendar_event.pickup_address">
                      N/A
                    </div>
                  </v-col>

                  <!-- Dropoff Location -->
                  <v-col cols="12" class="mt-3">
                    <div class="text-caption grey--text">Dropoff Location:</div>
                    <div class="text-body-2 font-weight-medium" v-if="trip.calendar_event.dropoff_address">
                      {{ trip.calendar_event.dropoff_address.address }}
                    </div>
                    <div class="text-caption grey--text" v-if="!trip.calendar_event.dropoff_address">
                      N/A
                    </div>
                  </v-col>

                  <!-- Scheduled Time -->
                  <v-col cols="6" class="mt-3">
                    <div class="text-caption grey--text">Scheduled Date:</div>
                    <div class="text-body-2 font-weight-medium">{{ formatDate(trip.calendar_event.event_date) }}</div>
                  </v-col>
                  <v-col cols="6" class="mt-3">
                    <div class="text-caption grey--text">Time:</div>
                    <div class="text-body-2 font-weight-medium">{{ formatTime(trip.calendar_event.start_time) }}</div>
                  </v-col>

                  <!-- Materials -->
                  <v-col cols="12" class="mt-3" v-if="trip.calendar_event.materials && trip.calendar_event.materials.length > 0">
                    <div class="text-caption grey--text">To Pickup:</div>
                    <v-chip 
                      v-for="material in trip.calendar_event.materials" 
                      :key="material.id" 
                      small 
                      class="mr-2 mt-1"
                      @click="openQRModal(material)"
                      style="cursor: pointer;"
                    >
                      <v-icon left x-small>mdi-qrcode</v-icon>
                      {{ material.title }}
                    </v-chip>
                  </v-col>

                  <!-- Estimated Distance & Duration -->
                  <v-col cols="12" class="mt-3" v-if="trip.estimated_distance || trip.estimated_duration">
                    <v-divider class="my-2"></v-divider>
                    <div class="d-flex align-center justify-space-between">
                      <div v-if="trip.estimated_distance">
                        <div class="text-caption grey--text">Estimated Distance:</div>
                        <div class="text-body-2 font-weight-medium">
                          <v-icon small color="blue" class="mr-1">mdi-map-marker-distance</v-icon>
                          {{ trip.estimated_distance.toFixed(1) }} km
                        </div>
                      </div>
                      <div v-if="trip.estimated_duration">
                        <div class="text-caption grey--text">Estimated Duration:</div>
                        <div class="text-body-2 font-weight-medium">
                          <v-icon small color="orange" class="mr-1">mdi-clock-outline</v-icon>
                          {{ trip.estimated_duration }} min
                        </div>
                      </div>
                    </div>
                  </v-col>

                  <!-- Notes -->
                  <v-col cols="12" class="mt-3" v-if="trip.notes || trip.calendar_event.notes">
                    <div class="text-caption grey--text">Notes:</div>
                    <div class="text-body-2">{{ trip.notes || trip.calendar_event.notes }}</div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <!-- Trip Map & Actions -->
            <v-card class="mb-4 trip-card" elevation="0">
              <v-card-title class="pb-2">
                <v-icon color="#10B981" class="mr-2">mdi-map-marker-path</v-icon>
                <span style="color: #10B981;">Trip Actions</span>
              </v-card-title>
              <v-card-text>
                <!-- Open Map Button -->
                <v-btn
                  block
                  large
                  outlined
                  color="#10B981"
                  class="text-none mb-3"
                  @click="toggleMap(trip.id)"
                >
                  <v-icon left>{{ mapOpen[trip.id] ? 'mdi-map-marker-off' : 'mdi-map' }}</v-icon>
                  {{ mapOpen[trip.id] ? 'Close Map' : 'Open Map' }}
                </v-btn>

                <!-- Google Map -->
                <div v-if="mapOpen[trip.id]" class="mb-3">
                  <TripMap
                    :start-location="getStartLocation(trip)"
                    :end-location="getEndLocation(trip)"
                    :trip-status="trip.status"
                    :show-route-to-start="(trip.status === 'ASSIGNED' || trip.status === 'ON_ROUTE') && !deliveryInProgress[trip.id]"
                    :show-route-to-end="deliveryInProgress[trip.id] || false"
                    @location-update="handleLocationUpdate"
                    @route-info="handleRouteInfo(trip.id, $event)"
                    @route-error="handleRouteError"
                  />
                </div>

                <!-- Trip Timer (shown during delivery) -->
                <v-card v-if="deliveryInProgress[trip.id]" class="mb-3 pa-3" elevation="1" color="#FFF3E0">
                  <div class="d-flex align-center justify-center">
                    <v-icon color="#F57C00" class="mr-2">mdi-timer</v-icon>
                    <div>
                      <div class="text-caption grey--text">Time Elapsed</div>
                      <div class="text-h6 font-weight-bold" style="color: #F57C00;">
                        {{ formatElapsedTime(tripElapsedTimes[trip.id] || 0) }}
                      </div>
                    </div>
                  </div>
                </v-card>

                <!-- Status Update Buttons -->
                <v-btn
                  v-if="trip.status === 'ASSIGNED'"
                  block
                  large
                  color="#10B981"
                  dark
                  class="text-none mb-2"
                  @click="updateStatus(trip.id, 'ON_ROUTE')"
                  :loading="updatingStatus"
                >
                  <v-icon left>mdi-navigation</v-icon>
                  Start Trip to Pickup
                </v-btn>

                <v-btn
                  v-if="trip.status === 'ON_ROUTE'"
                  block
                  large
                  color="#FF9800"
                  dark
                  class="text-none mb-2"
                  @click="updateStatus(trip.id, 'ARRIVED')"
                  :loading="updatingStatus"
                >
                  <v-icon left>mdi-map-marker-check</v-icon>
                  Arrived at Pickup
                </v-btn>

                <v-btn
                  v-if="trip.status === 'ARRIVED' && !deliveryInProgress[trip.id]"
                  block
                  large
                  color="#2563EB"
                  dark
                  class="text-none mb-2"
                  @click="startDelivery(trip.id)"
                  :loading="updatingStatus"
                >
                  <v-icon left>mdi-truck-fast</v-icon>
                  Begin Delivery
                </v-btn>

                <v-btn
                  v-if="trip.status === 'ARRIVED' && deliveryInProgress[trip.id]"
                  block
                  large
                  color="#4CAF50"
                  dark
                  class="text-none mb-2"
                  @click="completeDelivery(trip.id)"
                  :loading="updatingStatus"
                >
                  <v-icon left>mdi-check-circle</v-icon>
                  Complete Delivery
                </v-btn>
              </v-card-text>
            </v-card>

            <!-- Download Documents section for trip -->

            <!-- Gate Passes & Documents -->
            <v-card class="mb-4 documents-card" elevation="0">
              <v-card-title class="pb-3 px-4">
                <v-icon color="#8B5CF6" class="mr-2" size="20">mdi-file-document</v-icon>
                <span class="documents-title">Gate Passes & Documents</span>
              </v-card-title>
              <v-card-text class="px-4">
                <!-- File Inputs (Hidden) -->
                <!-- Camera input - opens camera on mobile devices -->
                <input
                  :ref="`cameraInput-${trip.id}`"
                  type="file"
                  accept="image/*"
                  capture="environment"
                  style="display: none"
                  aria-label="Take photo with camera"
                  @change="handleCameraCapture(trip.id, $event)"
                />
                <!-- File input - opens file picker -->
                <input
                  :ref="`fileInput-${trip.id}`"
                  type="file"
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                  multiple
                  style="display: none"
                  aria-label="Upload files"
                  @change="handleFileUpload(trip.id, $event)"
                />

                <!-- Upload Buttons -->
                <div class="upload-buttons-grid">
                  <div 
                    class="upload-card"
                    role="button"
                    tabindex="0"
                    @click="$refs[`cameraInput-${trip.id}`][0].click()"
                    @keyup.enter="$refs[`cameraInput-${trip.id}`][0].click()"
                    @keyup.space="$refs[`cameraInput-${trip.id}`][0].click()"
                    :class="{ 'disabled': uploadingFiles[trip.id] }"
                  >
                    <div class="upload-icon-wrapper">
                      <v-icon color="#8B5CF6" size="24">mdi-camera</v-icon>
                    </div>
                    <div class="upload-text">Take Photo</div>
                  </div>

                  <div 
                    class="upload-card dashed"
                    role="button"
                    tabindex="0"
                    @click="$refs[`fileInput-${trip.id}`][0].click()"
                    @keyup.enter="$refs[`fileInput-${trip.id}`][0].click()"
                    @keyup.space="$refs[`fileInput-${trip.id}`][0].click()"
                    :class="{ 'disabled': uploadingFiles[trip.id] }"
                  >
                    <div class="upload-icon-wrapper">
                      <v-icon color="#8B5CF6" size="24">mdi-upload</v-icon>
                    </div>
                    <div class="upload-text">Upload Files</div>
                  </div>
                </div>

                <!-- Upload Progress -->
                <v-progress-linear
                  v-if="uploadingFiles[trip.id]"
                  indeterminate
                  color="#8B5CF6"
                  class="mt-3"
                ></v-progress-linear>

                <!-- Uploaded Documents List -->
                <div v-if="tripDocuments[trip.id] && tripDocuments[trip.id].length > 0" class="mt-4">
                  <v-divider class="mb-3"></v-divider>
                  <div class="text-caption grey--text mb-2">
                    Documents ({{ tripDocuments[trip.id].length }})
                  </div>
                  <v-list dense class="documents-list">
                    <v-list-item
                      v-for="(doc, index) in tripDocuments[trip.id]"
                      :key="index"
                      class="document-item"
                    >
                      <v-list-item-avatar>
                        <v-icon :color="getFileIconColor(doc.name)">
                          {{ getFileIcon(doc.name) }}
                        </v-icon>
                      </v-list-item-avatar>
                      
                      <v-list-item-content>
                        <v-list-item-title class="text-body-2">
                          {{ doc.name }}
                        </v-list-item-title>
                        <v-list-item-subtitle class="text-caption">
                          {{ formatUploadTime(doc.created_at) }}
                        </v-list-item-subtitle>
                      </v-list-item-content>

                      <v-list-item-action class="flex-row">
                        <!-- Download button -->
                        <v-btn
                          icon
                          small
                          @click="downloadDocument(doc)"
                        >
                          <v-icon small color="success">mdi-download</v-icon>
                        </v-btn>
                      </v-list-item-action>
                    </v-list-item>
                  </v-list>
                </div>
              </v-card-text>
            </v-card>
          </v-card-text>
        </v-tab-item>
      </v-tabs-items>
    </v-card>

    <!-- QR Code Modal -->
    <v-dialog v-model="showQRModal" max-width="500">
      <v-card>
        <v-card-title class="pb-2">
          <v-icon color="primary" class="mr-2">mdi-qrcode</v-icon>
          <span>Material QR Code</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="showQRModal = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="text-center pt-4" v-if="selectedMaterial">
          <div class="text-h6 mb-4">{{ selectedMaterial.title }}</div>
          <QRCodeGenerator
            :data="getMaterialQRData(selectedMaterial)"
            :size="300"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Error Snackbar -->
    <v-snackbar v-model="showError" color="error" :timeout="5000" top>
      {{ errorMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="showError = false">Close</v-btn>
      </template>
    </v-snackbar>

    <!-- Tracking Warning Snackbar -->
    <v-snackbar v-model="showTrackingWarning" color="warning" :timeout="4000" top>
      {{ trackingWarningMsg }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import TripMap from './TripMap.vue';
import QRCodeGenerator from '../shared/QRCodeGenerator.vue';

export default {
  name: 'DriverDashboard',

  components: {
    TripMap,
    QRCodeGenerator,
  },

  data() {
    return {
      activeTab: 0,
      updatingStatus: false,
      showError: false,
      errorMessage: '',
      mapOpen: {}, // Track which trip maps are open
      deliveryInProgress: {}, // Track if delivery has started for each trip
      tripTimers: {}, // Store timer intervals for each trip
      tripStartTimes: {}, // Store start times for each delivery
      tripElapsedTimes: {}, // Store elapsed time in seconds for each trip
      tripRouteInfo: {}, // Store route distance and duration for each trip
      uploadingFiles: {}, // Track upload status for each trip
      tripDocuments: {}, // Store uploaded documents for each trip
      // Tracking resilience
      lastKnownLocation: null,
      wakeLock: null,
      wakeLockActive: false,
      showTrackingWarning: false,
      trackingWarningMsg: '',
      // Background tracking (independent of map)
      geoWatchId: null,
      geoLastSentAt: 0,
      geoMinIntervalMs: 15000, // send at most every 15s
      geoMinDistanceKm: 0.02, // ~20m movement filter
      // QR Code Modal
      showQRModal: false,
      selectedMaterial: null,
    };
  },

  computed: {
    ...mapState('driver', ['trips', 'loading', 'error']),
    ...mapGetters('driver', ['isAuthenticated']),

    driverInfo() {
      try {
        const info = localStorage.getItem('driver_info');
        return info ? JSON.parse(info) : {};
      } catch (error) {
        console.error('Error loading driver info:', error);
        return {};
      }
    },

    statusColor() {
      const colors = {
        AVAILABLE: '#10B981',
        BUSY: '#F59E0B',
        OFF_DUTY: '#6B7280',
      };
      return colors[this.driverInfo.status] || '#6B7280';
    },
  },

  methods: {
    ...mapActions('driver', ['loadDriver', 'fetchActiveTrips', 'updateTripStatus', 'updateLocation']),

    // Compute start location from trip data or calendar event
    getStartLocation(trip) {
      if (trip.start_location && trip.start_location.lat && trip.start_location.lng) {
        return trip.start_location;
      }
      // Fallback to pickup_address from calendar_event
      if (trip.calendar_event && trip.calendar_event.pickup_address) {
        const addr = trip.calendar_event.pickup_address;
        // Check for location_latitude/location_longitude (database fields)
        if (addr.location_latitude && addr.location_longitude) {
          return {
            lat: parseFloat(addr.location_latitude),
            lng: parseFloat(addr.location_longitude),
            address: addr.address || `${addr.city || ''}, ${addr.country || ''}`.trim()
          };
        }
        // Fallback to latitude/longitude if they exist
        if (addr.latitude && addr.longitude) {
          return {
            lat: parseFloat(addr.latitude),
            lng: parseFloat(addr.longitude),
            address: addr.address || `${addr.city || ''}, ${addr.country || ''}`.trim()
          };
        }
      }
      return null;
    },

    // Compute end location from trip data or calendar event
    getEndLocation(trip) {
      if (trip.end_location && trip.end_location.lat && trip.end_location.lng) {
        return trip.end_location;
      }
      // Fallback to dropoff_address from calendar_event
      if (trip.calendar_event && trip.calendar_event.dropoff_address) {
        const addr = trip.calendar_event.dropoff_address;
        // Check for location_latitude/location_longitude (database fields)
        if (addr.location_latitude && addr.location_longitude) {
          return {
            lat: parseFloat(addr.location_latitude),
            lng: parseFloat(addr.location_longitude),
            address: addr.address || `${addr.city || ''}, ${addr.country || ''}`.trim()
          };
        }
        // Fallback to latitude/longitude if they exist
        if (addr.latitude && addr.longitude) {
          return {
            lat: parseFloat(addr.latitude),
            lng: parseFloat(addr.longitude),
            address: addr.address || `${addr.city || ''}, ${addr.country || ''}`.trim()
          };
        }
      }
      return null;
    },

    toggleMap(tripId) {
      this.$set(this.mapOpen, tripId, !this.mapOpen[tripId]);
    },

    handleLocationUpdate(location) {
      // Update driver location in backend
      this.updateLocation({ lat: location.lat, lng: location.lng });
      // Cache last known location for pagehide/visibilitychange keepalive
      this.lastKnownLocation = { lat: location.lat, lng: location.lng };
    },

    // Background geolocation independent from map component
    startBackgroundTracking() {
      if (!('geolocation' in navigator)) return;
      if (this.geoWatchId) return; // already active
      if (!this.isTrackingActive()) return; // only when an active trip movement
      this.geoWatchId = navigator.geolocation.watchPosition(
        (pos) => {
          const now = Date.now();
          const location = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          // Rate & movement filters
          const intervalOk = (now - this.geoLastSentAt) > this.geoMinIntervalMs;
          let movedEnough = true;
          if (this.lastKnownLocation) {
            // Simple distance check using approximate km per degree
            const latDiff = Math.abs(location.lat - this.lastKnownLocation.lat);
            const lngDiff = Math.abs(location.lng - this.lastKnownLocation.lng);
            const approxDistKm = Math.sqrt(latDiff * latDiff + lngDiff * lngDiff) * 111; // ~111 km per degree
            movedEnough = approxDistKm >= this.geoMinDistanceKm;
          }
          if (intervalOk && movedEnough) {
            this.updateLocation(location);
            this.lastKnownLocation = location;
            this.geoLastSentAt = now;
          }
        },
        (err) => {
          // Silent for permission denied
          if (err.code !== 1) {
            console.warn('Background geo error:', err.message);
          }
        },
        { enableHighAccuracy: true, maximumAge: 5000, timeout: 10000 }
      );
    },

    stopBackgroundTracking() {
      if (this.geoWatchId && 'geolocation' in navigator) {
        navigator.geolocation.clearWatch(this.geoWatchId);
      }
      this.geoWatchId = null;
    },

    refreshBackgroundTracking() {
      // Restart watcher when status changes
      if (this.isTrackingActive()) {
        if (!this.geoWatchId) {
          this.startBackgroundTracking();
        }
      } else {
        this.stopBackgroundTracking();
      }
    },

    // Determine if any trip is in an active movement phase
    isTrackingActive() {
      if (!this.trips || this.trips.length === 0) return false;
      // Active when any trip is ON_ROUTE, ARRIVED (during site), or deliveryInProgress
      return this.trips.some(t => ['ON_ROUTE', 'ARRIVED'].includes(t.status)) ||
             Object.values(this.deliveryInProgress).some(Boolean);
    },

    // Wake Lock controls to keep screen on during active trips
    async enableWakeLock() {
      try {
        if ('wakeLock' in navigator && !this.wakeLockActive) {
          this.wakeLock = await navigator.wakeLock.request('screen');
          this.wakeLockActive = true;
          this.wakeLock.addEventListener('release', () => {
            this.wakeLockActive = false;
          });
        }
      } catch (e) {
        // Non-fatal; just log
        console.warn('Wake Lock request failed:', e?.message || e);
      }
    },

    async disableWakeLock() {
      try {
        if (this.wakeLock) {
          await this.wakeLock.release();
        }
      } catch (e) {
        // ignore
      } finally {
        this.wakeLock = null;
        this.wakeLockActive = false;
      }
    },

    // Re-request wake lock when returning to the page (some browsers release on hide)
    async handleVisibilityChange() {
      if (document.visibilityState === 'hidden') {
        if (this.isTrackingActive()) {
          this.trackingWarningMsg = 'Warning: Location tracking may pause while this tab is in the background. Please keep the screen active.';
          this.showTrackingWarning = true;
          // Try to send a last-known location to keep backend fresh
          this.sendLastLocationKeepalive();
        }
      } else if (document.visibilityState === 'visible') {
        if (this.isTrackingActive()) {
          this.enableWakeLock();
        }
      }
    },

    // Use keepalive fetch to send the last known location when the page is being hidden/unloaded
    async sendLastLocationKeepalive() {
      try {
        const loc = this.lastKnownLocation;
        if (!loc) return;
        const token = this.$store.getters['driver/driverToken'];
        const url = `${process.env.VUE_APP_API_URL}api/driver/location`;
        await fetch(url, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ lat: loc.lat, lng: loc.lng }),
          keepalive: true,
        });
      } catch (e) {
        // Silent failure; unloading context is fragile
      }
    },
    
    handleRouteInfo(tripId, routeInfo) {
      // Store route information for the trip (distance in meters, duration in seconds)
      this.$set(this.tripRouteInfo, tripId, {
        distanceMeters: routeInfo.distance,
        durationSeconds: routeInfo.duration,
        distanceKm: (routeInfo.distance / 1000).toFixed(2),
        durationMinutes: Math.round(routeInfo.duration / 60),
        distanceText: routeInfo.distanceText,
        durationText: routeInfo.durationText
      });
    },

    handleRouteError(error) {
      // Display route error to user
      this.errorMessage = error.message;
      this.showError = true;
    },

    async startDelivery(tripId) {
      this.updatingStatus = true;
      try {
        // Clear old route info - we need fresh route from pickup to dropoff
        this.$delete(this.tripRouteInfo, tripId);
        
        // Mark delivery as in progress FIRST
        // This will trigger TripMap to draw route from pickup to dropoff
        this.$set(this.deliveryInProgress, tripId, true);
        
        // Auto-open map if not already open
        if (!this.mapOpen[tripId]) {
          this.$set(this.mapOpen, tripId, true);
        }
        
        // Wait for route to be calculated
        // TripMap will emit route-info event when route is ready
        await new Promise(resolve => {
          const checkRoute = setInterval(() => {
            const routeData = this.tripRouteInfo[tripId];
            if (routeData && routeData.distanceKm) {
              clearInterval(checkRoute);
              resolve();
            }
          }, 100);
          
          // Timeout after 5 seconds
          setTimeout(() => {
            clearInterval(checkRoute);
            resolve();
          }, 5000);
        });
        
        // Get route info (distance and duration) from map
        const routeData = this.tripRouteInfo[tripId] || {};
        
        // Get trip details for logging
        const trip = this.trips.find(t => t.id === tripId);
        console.log('🚚 Starting delivery for trip:', tripId);
        console.log('📍 Start Location (Pickup):', trip?.start_location);
        console.log('📍 End Location (Dropoff):', trip?.end_location);
        console.log('🗺️ Route Data from Map (FRESH):', routeData);
        console.log('   Distance:', routeData.distanceKm, 'km');
        console.log('   Duration:', routeData.durationMinutes, 'min');
        
        // Call backend to update calendar event status to ON_WAY_TO_WAREHOUSE
        // and save estimated distance/duration from Google Maps
        await this.$store.dispatch('driver/startDelivery', {
          tripId,
          estimated_distance: routeData.distanceKm ? parseFloat(routeData.distanceKm) : null,
          estimated_duration: routeData.durationMinutes || null
        });
        
        // Record start time
        this.$set(this.tripStartTimes, tripId, Date.now());
        this.$set(this.tripElapsedTimes, tripId, 0);
        
        // Start timer
        const timer = setInterval(() => {
          const elapsed = Math.floor((Date.now() - this.tripStartTimes[tripId]) / 1000);
          this.$set(this.tripElapsedTimes, tripId, elapsed);
        }, 1000);
        
        this.$set(this.tripTimers, tripId, timer);
        
        // Auto-open map if not already open
        if (!this.mapOpen[tripId]) {
          this.$set(this.mapOpen, tripId, true);
        }

        // Keep device awake while delivery is in progress
        this.enableWakeLock();
        // Ensure background tracking active
        this.refreshBackgroundTracking();
      } catch (error) {
        // If it's an auth error (401), the store will handle redirect
        // Only show error if it's not an auth error
        if (error.response?.status !== 401) {
          this.errorMessage = error.response?.data?.message || 'Failed to start delivery';
          this.showError = true;
        }
      } finally {
        this.updatingStatus = false;
      }
    },

    async completeDelivery(tripId) {
      this.updatingStatus = true;
      try {
        // Stop timer
        if (this.tripTimers[tripId]) {
          clearInterval(this.tripTimers[tripId]);
          this.$set(this.tripTimers, tripId, null);
        }
        
        // Calculate final duration in minutes
        const durationMinutes = Math.round(this.tripElapsedTimes[tripId] / 60);
        
        // Update trip status to COMPLETED with actual delivery duration
        // Distance will be calculated from estimated_distance (Google Maps) on backend
        await this.updateTripStatus({ 
          tripId, 
          status: 'COMPLETED',
          actual_duration: durationMinutes
        });
        
        // Clean up
        this.$set(this.deliveryInProgress, tripId, false);
        this.$set(this.tripStartTimes, tripId, null);
        this.$set(this.tripElapsedTimes, tripId, 0);

        // Release wake lock when delivery completes (no more active tracking)
        if (!this.isTrackingActive()) {
          this.disableWakeLock();
        }
        this.refreshBackgroundTracking();
        
        // Trip will be removed from list automatically
      } catch (error) {
        // If it's an auth error (401), the store will handle redirect
        // Only show error if it's not an auth error
        if (error.response?.status !== 401) {
          this.errorMessage = error.response?.data?.message || 'Failed to complete delivery';
          this.showError = true;
          
          // Restart timer if error occurred
          if (this.tripStartTimes[tripId]) {
            const timer = setInterval(() => {
              const elapsed = Math.floor((Date.now() - this.tripStartTimes[tripId]) / 1000);
              this.$set(this.tripElapsedTimes, tripId, elapsed);
            }, 1000);
            this.$set(this.tripTimers, tripId, timer);
          }
        }
      } finally {
        this.updatingStatus = false;
      }
    },

    async updateStatus(tripId, newStatus) {
      this.updatingStatus = true;
      try {
        await this.updateTripStatus({ tripId, status: newStatus });
        
        // Auto-open map when starting trip to pickup
        if (newStatus === 'ON_ROUTE' && !this.mapOpen[tripId]) {
          this.$set(this.mapOpen, tripId, true);
        }

        // Engage wake lock when starting movement towards pickup
        if (newStatus === 'ON_ROUTE') {
          this.enableWakeLock();
        }
        // Re-evaluate background tracking need
        this.refreshBackgroundTracking();
        
        // Trip will be automatically removed from list if completed or cancelled
      } catch (error) {
        // If it's an auth error (401), the store will handle redirect
        // Only show error if it's not an auth error
        if (error.response?.status !== 401) {
          this.errorMessage = error.response?.data?.message || 'Failed to update trip status';
          this.showError = true;
        }
      } finally {
        this.updatingStatus = false;
      }
    },

    formatElapsedTime(seconds) {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const secs = seconds % 60;
      
      if (hours > 0) {
        return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
      }
      return `${minutes}:${secs.toString().padStart(2, '0')}`;
    },

    getTripStatusColor(status) {
      const colors = {
        ASSIGNED: '#2196F3',
        ON_ROUTE: '#FF9800',
        ARRIVED: '#9C27B0',
        COMPLETED: '#4CAF50',
        CANCELLED: '#757575',
      };
      return colors[status] || '#757575';
    },

    getTripStatusIcon(status) {
      const icons = {
        ASSIGNED: 'mdi-clipboard-check',
        ON_ROUTE: 'mdi-truck-fast',
        ARRIVED: 'mdi-map-marker-check',
        COMPLETED: 'mdi-check-circle',
        CANCELLED: 'mdi-close-circle',
      };
      return icons[status] || 'mdi-help-circle';
    },

    formatStatus(status) {
      return status.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
    },

    formatDate(date) {
      if (!date) return 'N/A';
      const d = new Date(date);
      return d.toLocaleDateString('en-US', {
        timeZone: 'Asia/Dubai',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    },

    formatTime(time) {
      if (!time) return 'N/A';
      // time is in HH:MM:SS format
      const [hours, minutes] = time.split(':');
      const hour = parseInt(hours, 10);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minutes} ${ampm}`;
    },

    async loadTripDocuments(tripId) {
      try {
        // Fetch all documents for the trip (includes both event and trip uploads)
        const token = this.$store.getters['driver/driverToken'];
        const response = await fetch(
          `${process.env.VUE_APP_API_URL}api/driver/trips/${tripId}/documents`,
          {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            this.$set(this.tripDocuments, tripId, data.data);
          }
        }
      } catch (error) {
        console.error('Failed to load trip documents:', error);
      }
    },

    async handleCameraCapture(tripId, event) {
      await this.processFiles(tripId, event.target.files);
      event.target.value = ''; // Reset input
    },

    async handleFileUpload(tripId, event) {
      await this.processFiles(tripId, event.target.files);
      event.target.value = ''; // Reset input
    },

    async processFiles(tripId, files) {
      if (!files || files.length === 0) return;

      this.$set(this.uploadingFiles, tripId, true);

      const filesArray = Array.from(files);
      const maxSize = 11 * 1024 * 1024; // 11MB
      const allowedTypes = [
        'image/jpeg', 'image/jpg', 'image/png', 'image/gif',
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ];

      // Validate and prepare files
      const validFiles = filesArray.filter((file) => {
        // Validate file size
        if (file.size > maxSize) {
          this.errorMessage = `File "${file.name}" exceeds 11MB limit`;
          this.showError = true;
          return false;
        }

        // Validate file type
        if (!allowedTypes.includes(file.type)) {
          this.errorMessage = `File "${file.name}" has an unsupported type`;
          this.showError = true;
          return false;
        }

        return true;
      });

      // Convert all valid files to base64
      const documentPromises = validFiles.map(async (file) => {
        const base64 = await this.fileToBase64(file);
        return {
          name: file.name,
          body: base64
        };
      });

      const documents = await Promise.all(documentPromises);

      // Upload all documents in a single request (like material images)
      try {
        await this.uploadDocument(tripId, documents);
        
        this.$set(this.uploadingFiles, tripId, false);
        
        // Reload documents for this trip
        await this.loadTripDocuments(tripId);
        
        console.log(`Successfully uploaded ${documents.length} document(s)`);
      } catch (error) {
        this.$set(this.uploadingFiles, tripId, false);
        this.errorMessage = `Failed to upload document(s): ${error.message}`;
        this.showError = true;
      }
    },

    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = (error) => reject(error);
        reader.readAsDataURL(file);
      });
    },

    async uploadDocument(tripId, documentsArray) {
      try {
        const token = this.$store.getters['driver/driverToken'];
        const response = await fetch(`${process.env.VUE_APP_API_URL}api/driver/trips/${tripId}/documents`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ documents: documentsArray })
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error || 'Upload failed');
        }

        return await response.json();
      } catch (error) {
        throw new Error(error.message || 'Upload failed');
      }
    },

    getFileIcon(fileName) {
      const ext = fileName.split('.').pop().toLowerCase();
      if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) {
        return 'mdi-file-image';
      }
      if (ext === 'pdf') {
        return 'mdi-file-pdf-box';
      }
      if (['doc', 'docx'].includes(ext)) {
        return 'mdi-file-document';
      }
      if (['xls', 'xlsx'].includes(ext)) {
        return 'mdi-file-excel';
      }
      return 'mdi-file';
    },

    getFileIconColor(fileName) {
      const ext = fileName.split('.').pop().toLowerCase();
      if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) {
        return 'success';
      }
      if (ext === 'pdf') {
        return 'error';
      }
      if (['doc', 'docx'].includes(ext)) {
        return 'primary';
      }
      if (['xls', 'xlsx'].includes(ext)) {
        return 'green';
      }
      return 'grey';
    },

    formatUploadTime(timestamp) {
      if (!timestamp) return '';
      const date = new Date(timestamp);
      const now = new Date();
      const diff = now - date;

      // Less than 1 minute
      if (diff < 60000) {
        return 'Just now';
      }
      // Less than 1 hour
      if (diff < 3600000) {
        const mins = Math.floor(diff / 60000);
        return `${mins} min${mins > 1 ? 's' : ''} ago`;
      }
      // Less than 24 hours
      if (diff < 86400000) {
        const hours = Math.floor(diff / 3600000);
        return `${hours} hour${hours > 1 ? 's' : ''} ago`;
      }
      // Format as date in UAE timezone
      return date.toLocaleDateString('en-US', {
        timeZone: 'Asia/Dubai',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    async downloadDocument(doc) {
      try {
        // Fetch the file from S3
        const response = await fetch(doc.url);
        if (!response.ok) {
          throw new Error('Failed to download document');
        }

        // Get the blob
        const blob = await response.blob();

        // Create a download link
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = doc.name;
        document.body.appendChild(link);
        link.click();
        
        // Cleanup
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
      } catch (error) {
        console.error('Error downloading document:', error);
        this.errorMessage = `Failed to download ${doc.name}`;
        this.showError = true;
      }
    },

    openQRModal(material) {
      this.selectedMaterial = material;
      this.showQRModal = true;
    },

    getMaterialQRData(material) {
      // Generate QR code data - can be a URL to the material page or the material ID
      // Adjust this based on your app's needs
      const baseUrl = window.location.origin;
      return `${baseUrl}/materials/${material.id}`;
    },
  },

  async mounted() {
    try {
      await this.loadDriver();
      await this.fetchActiveTrips();
      // Load documents for each trip
      this.trips.forEach(trip => {
        this.loadTripDocuments(trip.id);
      });
      // Start background tracking if any active movement state
      this.refreshBackgroundTracking();

      // Visibility + lifecycle handlers to improve tracking resilience
      this._visibilityHandler = this.handleVisibilityChange.bind(this);
      document.addEventListener('visibilitychange', this._visibilityHandler);
      // pagehide fires on tab close or navigation
      this._pageHideHandler = () => this.sendLastLocationKeepalive();
      window.addEventListener('pagehide', this._pageHideHandler);
      // beforeunload as a secondary signal (best-effort)
      this._beforeUnloadHandler = () => this.sendLastLocationKeepalive();
      window.addEventListener('beforeunload', this._beforeUnloadHandler);
    } catch (error) {
      // If authentication fails during mount, user will be redirected to login by the store
      console.log('Failed to load driver dashboard:', error);
    }
  },

  beforeDestroy() {
    // Clean up all timers
    Object.keys(this.tripTimers).forEach(tripId => {
      if (this.tripTimers[tripId]) {
        clearInterval(this.tripTimers[tripId]);
      }
    });

    // Remove lifecycle handlers
    if (this._visibilityHandler) {
      document.removeEventListener('visibilitychange', this._visibilityHandler);
    }
    if (this._pageHideHandler) {
      window.removeEventListener('pagehide', this._pageHideHandler);
    }
    if (this._beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this._beforeUnloadHandler);
    }
    // Release wake lock if active
    this.disableWakeLock();
    this.stopBackgroundTracking();
  },
};
</script>

<style scoped lang="scss">
.driver-dashboard {
  background-color: rgba(84, 88, 90, 0.2);
  max-width: 800px;
  margin: 0 auto;
  padding: 16px;
}

.info-card {
  background-color: #FFFFFF;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

.assignment-card {
  background-color: #EFF6FF;
  border: 1px solid #BFDBFE;
  border-radius: 8px;
  box-shadow: none;
}

.trip-card {
  background-color: #FFFFFF;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  box-shadow: none;
}

.pickup-card {
  background-color: #FFFFFF;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

.info-section {
  .info-label {
    font-size: 12px;
    color: #6B7280;
    font-weight: 500;
    margin-bottom: 4px;
  }

  .info-value {
    font-size: 14px;
    color: #111827;
    font-weight: 400;
    padding: 8px 0;
  }
}

.documents-card {
  background-color: #FFFFFF;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  box-shadow: none;

  .documents-title {
    font-size: 16px;
    font-weight: 600;
    color: #8B5CF6;
  }

  .upload-buttons-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    margin-bottom: 16px;
  }

  .upload-card {
    background-color: #FFFFFF;
    border: 2px solid #E5E7EB;
    border-radius: 8px;
    padding: 16px 12px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    min-height: 90px;
    outline: none;

    &.dashed {
      border-style: dashed;
      border-color: #D1D5DB;
    }

    &:hover:not(.disabled),
    &:focus:not(.disabled) {
      border-color: #8B5CF6;
      background-color: #F9FAFB;
      transform: translateY(-2px);
      box-shadow: 0 4px 6px rgba(139, 92, 246, 0.1);
    }

    &:focus:not(.disabled) {
      box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.2), 0 4px 6px rgba(139, 92, 246, 0.1);
    }

    &.disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .upload-icon-wrapper {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 44px;
      height: 44px;
      background-color: #F3F4F6;
      border-radius: 50%;
      transition: all 0.2s ease;
    }

    &:hover:not(.disabled) .upload-icon-wrapper {
      background-color: #EDE9FE;
    }

    .upload-text {
      font-size: 13px;
      font-weight: 500;
      color: #374151;
      text-align: center;
    }
  }

  .documents-list {
    background-color: transparent;

    .document-item {
      background-color: #F9FAFB;
      border-radius: 8px;
      margin-bottom: 8px;
      padding: 4px;

      &:hover {
        background-color: #F3F4F6;
      }

      ::v-deep .v-list-item__action {
        display: flex;
        flex-direction: row;
        align-items: center;
        margin: 0;
      }
    }
  }
}

.v-card {
  border-radius: 8px;
}

// Mobile-first optimizations
@media (max-width: 600px) {
  .driver-dashboard {
    padding: 12px;
  }

  .documents-card {
    .upload-buttons-grid {
      grid-template-columns: 1fr;
      gap: 10px;
    }

    .upload-card {
      min-height: 85px;
      padding: 14px 12px;
    }
  }
}
</style>

